import React from 'react';
import contensis from 'Common/api/contensis';
import { Op } from 'contensis-delivery-api';
import cmsParser from 'Common/util/cmsParser';
import setCacheHeaders from 'Common/func/setCacheHeaders';
import { excludeMetadata, transcriptEmbedFields } from 'Common/util/contensis/api';

import Metadata from 'Components/Metadata';
import MainLayout from 'Layout/Main';
import Template, { datamap, PageData } from 'Templates/Discover/52Weeks/Article';

import type { NextPageWithLayout, GetServerSideProps } from 'next';

const Week: NextPageWithLayout<PageData> = ({
    entry,
    ancestors,
    signUpPromo,
    prevWeek,
    nextWeek
}) => {
    return (
        <>
            <Metadata {...cmsParser.metadata(entry)} />
            <Template
                {...datamap({
                    entry,
                    ancestors,
                    signUpPromo,
                    prevWeek,
                    nextWeek
                })}
            />
        </>
    );
};

Week.getLayout = (page, props) => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

export const getServerSideProps: GetServerSideProps<any> = async ({ params, preview, res }) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });
    const { week } = params as { week: string };
    const { data, error } = await fetch(async (client, search) => {
        const resPage = await client.nodes.get({
            path: `/discover/52-weeks/${week}`,
            entryLinkDepth: 2,
            entryFields: [
                'articleMainVideo',
                'articleTitle',
                'articleSummary',
                'weekNumber',
                'date',
                'articleMainHeaderImage',
                'articleCategory',
                'canvas',
                'pageMetadata',
                'customTitle',
                'description',
                'keywords',
                'noIndexing',
                'canonical',
                'title',
                ...transcriptEmbedFields,
                ...excludeMetadata
            ]
        });

        if (!resPage.entry) {
            return { error: true };
        }

        const ancestors = await client.nodes.getAncestors({
            id: resPage.id,
            entryFields: ['title']
        });

        const weekNumber = resPage.entry.weekNumber;

        const prevWeekQuery = new Op.and(
            Op.equalTo('sys.contentTypeId', 'fiftyTwoWeeksContentPage'),
            Op.equalTo('weekNumber', weekNumber + 1)
        );

        const nextWeekQuery = new Op.and(
            Op.equalTo('sys.contentTypeId', 'fiftyTwoWeeksContentPage'),
            Op.equalTo('weekNumber', weekNumber - 1)
        );

        const cardFields = [
            'articleTitle',
            'articleSummary',
            'articleMainHeaderImage',
            'slug',
            'date',
            'weekNumber'
        ];

        const prevWeek = await search(prevWeekQuery, {
            fields: cardFields
        });

        const nextWeek = await search(nextWeekQuery, {
            fields: cardFields
        });

        const promoLinkQuery = new Op.equalTo('sys.contentTypeId', 'fiftyTwoWeeksLandingPage');

        const resfiftyTwoWeeksLP = await search(promoLinkQuery, {
            fields: ['signUpPromo', 'link', 'sys', 'url'],
            linkDepth: 1
        });

        return {
            entry: { ...resPage.entry },
            ancestors,
            signUpPromo: resfiftyTwoWeeksLP.items[0].signUpPromo,
            prevWeek: prevWeek.items[0] || null,
            nextWeek: nextWeek.items[0] || null
        };
    });

    if (error) {
        return { notFound: true };
    } else {
        setCacheHeaders(res, surrogateKeys);

        return { props: data };
    }
};

export default Week;
